/**
 * Alternative Product Modal
 * Custom element for showing alternative product recommendations when a variant is out of stock
 * Shows once per session using sessionStorage
 */

/* global subscribe */

if (!customElements.get('alt-product-modal')) {
  class AltProductModal extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.sessionKey = 'altProductModalShown';
    }

    connectedCallback() {
      if (this.initialized) return;

      this.dialog = this.querySelector('.alt-product-modal__dialog');
      this.overlay = this.querySelector('.alt-product-modal__overlay');
      this.closeButton = this.querySelector('.alt-product-modal__close');
      this.container = this.querySelector('.alt-product-modal__container');
      this.sizeElement = this.querySelector('.alt-product-modal__size');

      if (!this.dialog || !this.overlay || !this.closeButton) {
        console.warn('Alt Product Modal: Required elements not found');
        return;
      }

      this.sectionId = this.getAttribute('data-section-id');
      this.altProductData = this.loadAltProductData();

      if (!this.altProductData) {
        console.warn('Alt Product Modal: Alternative product data not found');
        return;
      }

      this.setupEventListeners();
      this.setupVariantChangeListener();
      this.initialized = true;
    }

    loadAltProductData() {
      const dataElement = document.getElementById(`AltProductData-${this.sectionId}`);
      if (!dataElement) return null;

      try {
        return JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error('Alt Product Modal: Failed to parse product data:', e);
        return null;
      }
    }

    setupEventListeners() {
      this.closeButton.addEventListener('click', this.closeModal.bind(this));
      this.overlay.addEventListener('click', this.closeModal.bind(this));

      this.handleEscape = (e) => {
        if (e.key === 'Escape' && !this.dialog.hidden) {
          this.closeModal();
        }
      };
      document.addEventListener('keydown', this.handleEscape);
    }

    setupVariantChangeListener() {
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribeVariant = subscribe(window.PUB_SUB_EVENTS.variantChange, (data) => {
          if (data && data.variant) {
            this.handleVariantChange(data.variant);
          }
        });
      }
    }

    handleVariantChange(variant) {
      // Check if modal has already been shown this session
      if (sessionStorage.getItem(this.sessionKey)) {
        return;
      }

      // Only proceed if variant is unavailable
      if (!variant || variant.available) {
        return;
      }

      // Get the selected size from variant options
      const selectedSize = this.getSelectedSize(variant);
      if (!selectedSize) return;

      // Check if alternative product has matching size in stock
      const matchingVariant = this.findMatchingVariant(selectedSize);

      if (matchingVariant && matchingVariant.available) {
        this.showModal(selectedSize);
        // Mark as shown for this session
        sessionStorage.setItem(this.sessionKey, 'true');
      }
    }

    getSelectedSize(variant) {
      // Variant options array contains the selected values for each option
      // For products with Size option, find the size value
      // Assuming size is typically the first or second option
      if (!variant.options || variant.options.length === 0) return null;

      // Try to find size in variant title or options
      // For Boreas/Hayward, size is typically the main option (M/L, 2X, etc.)
      return variant.options[0] || null;
    }

    findMatchingVariant(size) {
      if (!this.altProductData || !this.altProductData.variants) return null;

      // Find variant in alternative product that matches the size
      return this.altProductData.variants.find(variant => {
        // Check if any of the variant options matches the size
        return variant.options && variant.options.includes(size);
      });
    }

    showModal(size) {
      // Update size display
      if (this.sizeElement) {
        this.sizeElement.textContent = size;
      }

      // Show modal
      this.dialog.hidden = false;
      this.dialog.setAttribute('aria-hidden', 'false');

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Focus on modal for accessibility
      this.trapFocus();
    }

    closeModal() {
      this.dialog.hidden = true;
      this.dialog.setAttribute('aria-hidden', 'true');

      // Restore body scroll
      document.body.style.overflow = '';

      // Remove focus trap
      this.removeFocusTrap();
    }

    trapFocus() {
      // Get all focusable elements
      const focusableElements = this.container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      this.firstFocusable = focusableElements[0];
      this.lastFocusable = focusableElements[focusableElements.length - 1];

      // Focus first element (close button)
      this.firstFocusable.focus();

      // Trap focus within modal
      this.handleTab = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === this.firstFocusable) {
            e.preventDefault();
            this.lastFocusable.focus();
          }
        } else {
          if (document.activeElement === this.lastFocusable) {
            e.preventDefault();
            this.firstFocusable.focus();
          }
        }
      };

      document.addEventListener('keydown', this.handleTab);
    }

    removeFocusTrap() {
      if (this.handleTab) {
        document.removeEventListener('keydown', this.handleTab);
      }
    }

    disconnectedCallback() {
      // Cleanup event listeners
      if (this.handleEscape) {
        document.removeEventListener('keydown', this.handleEscape);
      }
      if (this.handleTab) {
        document.removeEventListener('keydown', this.handleTab);
      }
      if (this.unsubscribeVariant && typeof this.unsubscribeVariant === 'function') {
        this.unsubscribeVariant();
      }
    }
  }

  customElements.define('alt-product-modal', AltProductModal);
}
